import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { 
  CogIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const RBACConfigPage = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = user?.rules?.includes('super_admin');

  useEffect(() => {
    if (isSuperAdmin) {
      fetchConfig();
    }
  }, [isSuperAdmin]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/rbac-config`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch RBAC configuration');
      }

      const data = await response.json();
      setConfig(data);
    } catch (error) {
      setError('Failed to load RBAC configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/rbac-config`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      setSuccess('Configuration saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save configuration: ' + error.message);
    }
  };

  const createBackup = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/rbac-config/backup`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create backup');
      }

      setSuccess('Backup created successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to create backup: ' + error.message);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset to default configuration? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/rbac-config/reset`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to reset configuration');
      }

      setSuccess('Configuration reset to defaults');
      fetchConfig(); // Reload configuration
    } catch (error) {
      setError('Failed to reset configuration: ' + error.message);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-800 dark:text-red-300 mb-4">
            Super Admin Access Required
          </h2>
          <p className="text-red-600 dark:text-red-400">
            Only super administrators can access RBAC configuration management.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
            Failed to Load Configuration
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={fetchConfig}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'overview', name: 'Overview', icon: CogIcon },
    { key: 'roles', name: 'Roles', icon: UserGroupIcon },
    { key: 'permissions', name: 'Permissions', icon: ShieldCheckIcon },
    { key: 'vendors', name: 'Vendor Types', icon: BuildingOfficeIcon },
    { key: 'categories', name: 'Categories', icon: DocumentTextIcon }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              RBAC Configuration
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage roles, permissions, and access control settings
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={createBackup}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Create Backup
            </button>
            <button
              onClick={saveConfig}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              <CheckCircleIcon className="w-4 h-4 mr-2" />
              Save Changes
            </button>
            <button
              onClick={resetToDefaults}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
          <button 
            onClick={() => setError('')}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800">{success}</span>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="mb-8">
        <nav className="flex space-x-8 border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <OverviewTab config={config} />
        )}
        
        {activeTab === 'roles' && (
          <RolesTab 
            config={config} 
            setConfig={setConfig}
            onEdit={setEditingItem}
            onShowModal={setShowModal}
          />
        )}
        
        {activeTab === 'permissions' && (
          <PermissionsTab 
            config={config} 
            setConfig={setConfig}
            onEdit={setEditingItem}
            onShowModal={setShowModal}
          />
        )}
        
        {activeTab === 'vendors' && (
          <VendorTypesTab 
            config={config} 
            setConfig={setConfig}
            onEdit={setEditingItem}
            onShowModal={setShowModal}
          />
        )}
        
        {activeTab === 'categories' && (
          <CategoriesTab config={config} setConfig={setConfig} />
        )}
      </div>

      {/* Modal for editing */}
      {showModal && (
        <EditModal
          item={editingItem}
          config={config}
          setConfig={setConfig}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
          activeTab={activeTab}
        />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ config }) => {
  const roleCount = Object.keys(config.roles).length;
  const permissionCount = Object.keys(config.permissions).length;
  const vendorTypeCount = Object.keys(config.vendorTypes).length;
  const categoryCount = Object.keys(config.permissionCategories).length;

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <UserGroupIcon className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{roleCount}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Roles</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <ShieldCheckIcon className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{permissionCount}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Permissions</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <BuildingOfficeIcon className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{vendorTypeCount}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Vendor Types</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <DocumentTextIcon className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{categoryCount}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Categories</div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Info */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Configuration Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Version</div>
            <div className="text-gray-900 dark:text-white">{config.metadata?.version || 'Unknown'}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Last Modified</div>
            <div className="text-gray-900 dark:text-white">
              {config.metadata?.lastModified ? new Date(config.metadata.lastModified).toLocaleString() : 'Unknown'}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Modified By</div>
            <div className="text-gray-900 dark:text-white">{config.metadata?.modifiedBy || 'Unknown'}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</div>
            <div className="text-gray-900 dark:text-white">{config.metadata?.description || 'No description'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Roles Tab Component
const RolesTab = ({ config, setConfig, onEdit, onShowModal }) => {
  const addRole = () => {
    onEdit({
      type: 'role',
      isNew: true,
      data: {
        name: '',
        description: '',
        level: 1,
        permissions: []
      }
    });
    onShowModal(true);
  };

  const editRole = (roleKey, role) => {
    onEdit({
      type: 'role',
      isNew: false,
      key: roleKey,
      data: role
    });
    onShowModal(true);
  };

  const deleteRole = (roleKey) => {
    if (window.confirm(`Are you sure you want to delete the role "${roleKey}"?`)) {
      const newConfig = { ...config };
      delete newConfig.roles[roleKey];
      setConfig(newConfig);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Roles Management
        </h2>
        <button
          onClick={addRole}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Role
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(config.roles).map(([roleKey, role]) => (
          <div key={roleKey} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {role.name}
                </h3>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Level: {role.level}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => editRole(roleKey, role)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                {!['super_admin', 'admin', 'pending'].includes(roleKey) && (
                  <button
                    onClick={() => deleteRole(roleKey)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {role.description}
            </p>
            
            <div className="text-sm">
              <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions: {Array.isArray(role.permissions) ? role.permissions.length : (role.permissions === '*' ? 'All' : 0)}
              </div>
              {Array.isArray(role.permissions) && role.permissions.slice(0, 3).map((permission) => (
                <div key={permission} className="text-xs text-gray-500 dark:text-gray-400">
                  • {permission.replace(/_/g, ' ').toLowerCase()}
                </div>
              ))}
              {Array.isArray(role.permissions) && role.permissions.length > 3 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ... and {role.permissions.length - 3} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Permissions Tab Component
const PermissionsTab = ({ config, setConfig, onEdit, onShowModal }) => {
  const groupedPermissions = Object.entries(config.permissions).reduce((acc, [key, permission]) => {
    const category = permission.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push([key, permission]);
    return acc;
  }, {});

  const addPermission = () => {
    onEdit({
      type: 'permission',
      isNew: true,
      data: {
        name: '',
        description: '',
        category: 'general'
      }
    });
    onShowModal(true);
  };

  const editPermission = (permissionKey, permission) => {
    onEdit({
      type: 'permission',
      isNew: false,
      key: permissionKey,
      data: permission
    });
    onShowModal(true);
  };

  const deletePermission = (permissionKey) => {
    if (window.confirm(`Are you sure you want to delete the permission "${permissionKey}"?`)) {
      const newConfig = { ...config };
      delete newConfig.permissions[permissionKey];
      setConfig(newConfig);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Permissions Management
        </h2>
        <button
          onClick={addPermission}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Permission
        </button>
      </div>

      {Object.entries(groupedPermissions).map(([category, permissions]) => (
        <div key={category} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize">
            {category.replace(/_/g, ' ')} ({permissions.length})
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {permissions.map(([permissionKey, permission]) => (
              <div key={permissionKey} className="border border-gray-200 dark:border-gray-700 p-4 rounded">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                    {permission.name}
                  </h4>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => editPermission(permissionKey, permission)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <PencilIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deletePermission(permissionKey)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {permission.description}
                </p>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Key: {permissionKey}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Vendor Types Tab Component  
const VendorTypesTab = ({ config, setConfig, onEdit, onShowModal }) => {
  const addVendorType = () => {
    onEdit({
      type: 'vendorType',
      isNew: true,
      data: {
        name: '',
        description: '',
        permissions: []
      }
    });
    onShowModal(true);
  };

  const editVendorType = (vendorKey, vendorType) => {
    onEdit({
      type: 'vendorType',
      isNew: false,
      key: vendorKey,
      data: vendorType
    });
    onShowModal(true);
  };

  const deleteVendorType = (vendorKey) => {
    if (window.confirm(`Are you sure you want to delete the vendor type "${vendorKey}"?`)) {
      const newConfig = { ...config };
      delete newConfig.vendorTypes[vendorKey];
      setConfig(newConfig);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Vendor Types Management
        </h2>
        <button
          onClick={addVendorType}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Vendor Type
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(config.vendorTypes).map(([vendorKey, vendorType]) => (
          <div key={vendorKey} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {vendorType.name}
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => editVendorType(vendorKey, vendorType)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteVendorType(vendorKey)}
                  className="text-red-600 hover:text-red-800"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {vendorType.description}
            </p>
            
            <div className="text-sm">
              <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions: {vendorType.permissions?.length || 0}
              </div>
              {vendorType.permissions?.slice(0, 3).map((permission) => (
                <div key={permission} className="text-xs text-gray-500 dark:text-gray-400">
                  • {permission.replace(/_/g, ' ').toLowerCase()}
                </div>
              ))}
              {vendorType.permissions?.length > 3 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  ... and {vendorType.permissions.length - 3} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Categories Tab Component
const CategoriesTab = ({ config, setConfig }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Permission Categories
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(config.permissionCategories).map(([categoryKey, category]) => (
          <div key={categoryKey} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {category.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {category.description}
            </p>
            <div className="text-sm">
              <div className="font-medium text-gray-700 dark:text-gray-300">
                Icon: {category.icon}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ item, config, setConfig, onClose, activeTab }) => {
  const [formData, setFormData] = useState({
    ...item?.data,
    permissions: item?.data?.permissions || []
  });
  const [saving, setSaving] = useState(false);

  const availablePermissions = Object.keys(config?.permissions || {});

  const handlePermissionToggle = (permission) => {
    const currentPermissions = formData.permissions || [];
    const newPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter(p => p !== permission)
      : [...currentPermissions, permission];
    
    setFormData({...formData, permissions: newPermissions});
  };

  const handleSave = async () => {
    if (!item) return;

    setSaving(true);
    try {
      const newConfig = { ...config };

      if (item.type === 'role') {
        if (item.isNew) {
          // Add new role
          const roleKey = formData.name.toLowerCase().replace(/\s+/g, '_');
          newConfig.roles[roleKey] = {
            name: formData.name,
            description: formData.description,
            level: formData.level || 1,
            permissions: formData.permissions
          };
        } else {
          // Update existing role
          newConfig.roles[item.key] = {
            ...newConfig.roles[item.key],
            name: formData.name,
            description: formData.description,
            level: formData.level,
            permissions: formData.permissions
          };
        }
      } else if (item.type === 'permission') {
        if (item.isNew) {
          // Add new permission
          const permissionKey = formData.name.toLowerCase().replace(/\s+/g, '_');
          newConfig.permissions[permissionKey] = {
            name: formData.name,
            description: formData.description,
            category: formData.category || 'general'
          };
        } else {
          // Update existing permission
          newConfig.permissions[item.key] = {
            ...newConfig.permissions[item.key],
            name: formData.name,
            description: formData.description,
            category: formData.category
          };
        }
      } else if (item.type === 'vendorType') {
        if (item.isNew) {
          // Add new vendor type
          const vendorKey = formData.name.toLowerCase().replace(/\s+/g, '_');
          newConfig.vendorTypes[vendorKey] = {
            name: formData.name,
            description: formData.description,
            permissions: formData.permissions
          };
        } else {
          // Update existing vendor type
          newConfig.vendorTypes[item.key] = {
            ...newConfig.vendorTypes[item.key],
            name: formData.name,
            description: formData.description,
            permissions: formData.permissions
          };
        }
      }

      // Save to backend
      const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/rbac-config`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newConfig)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      // Update local state only after successful backend save
      setConfig(newConfig);
      onClose();
      
      // Show success message
      console.log('RBAC configuration saved successfully');
      
    } catch (error) {
      console.error('Failed to save RBAC configuration:', error);
      alert('Failed to save configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {item?.isNew ? 'Add' : 'Edit'} {item?.type}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows={3}
            />
          </div>

          {/* Role-specific fields */}
          {item?.type === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Level
              </label>
              <input
                type="number"
                value={formData.level || 1}
                onChange={(e) => setFormData({...formData, level: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="0"
                max="5"
              />
            </div>
          )}

          {/* Permission-specific fields */}
          {item?.type === 'permission' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={formData.category || 'general'}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="general">General</option>
                <option value="user_management">User Management</option>
                <option value="bidding">Bidding</option>
                <option value="tours">Tours</option>
                <option value="forms">Forms</option>
                <option value="calendar">Calendar</option>
                <option value="vendors">Vendors</option>
                <option value="system">System</option>
              </select>
            </div>
          )}

          {/* Permissions selection for roles and vendor types */}
          {(item?.type === 'role' || item?.type === 'vendorType') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions
              </label>
              <div className="max-h-64 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
                <div className="grid grid-cols-2 gap-2">
                  {availablePermissions.map((permission) => (
                    <label key={permission} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.permissions?.includes(permission) || false}
                        onChange={() => handlePermissionToggle(permission)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        {config?.permissions[permission]?.name || permission.replace(/_/g, ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Selected: {formData.permissions?.length || 0} permissions
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RBACConfigPage;
